/**
 * Le cahier : ce qui a été travaillé, et ce qu'il en reste (§6.8).
 *
 * Demandé par Hedi le 6 septembre 2026 : *« je veux pour chaque enfant avoir un
 * sous tab dans lequel je peux trouver les compétences travaillées (par défaut
 * cette semaine, temps configurable) ; les exercices travaillés, dispos par
 * compétence »*.
 *
 * **Ce n'est ni l'écran d'effort ni la fiche.** L'effort donne des totaux — huit
 * exercices, trois heures — sans jamais montrer un énoncé ; la fiche donne un
 * niveau sans dire ce qui a été fait pour l'atteindre. Celui-ci ouvre le travail
 * lui-même : le parent lit l'énoncé que son enfant a eu sous les yeux.
 *
 * **Le corrigé n'y est pas, et ce n'est pas un choix d'affichage** : l'API ne
 * l'envoie pas (`core-api`, `WorkedExerciseOut`). Cet écran s'ouvre à côté de
 * l'enfant aussi souvent qu'ailleurs, et un corrigé lu par-dessus l'épaule est
 * un corrigé servi sans tentative (règle 2).
 *
 * **Deux chiffres de réserve, pas un.** « En stock » compte les exercices
 * vérifiés de la compétence ; « jamais servis » retire ceux que cet enfant a
 * déjà vus. Un seul chiffre — le second — se lirait « il ne reste rien » sur une
 * compétence qui en compte trente, parce qu'un exercice déjà vu redevient
 * tirable après le délai anti-répétition.
 */

import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { call } from "@/api/client";
import { ApiError } from "@/api/errors";
import type { CompetencyWorkbookOut, WorkbookReport, WorkedExerciseOut } from "@/api/schema";
import { bornes, duree, enClair, jour, type Periode, type Preselection } from "@/parent/effort";
import { SelecteurDePeriode } from "@/ui/SelecteurDePeriode";
import { Card, Empty, ErrorNote, Loading, Screen } from "@/ui/primitives";
import { etiquette } from "@/ui/matieres";
import { TexteMathematique } from "@/ui/TexteMathematique";

/** *« par défaut cette semaine »*, et la même fenêtre que le tableau de bord. */
const DEFAUT: Preselection = "7j";

export function CahierScreen() {
  const { childId = "" } = useParams();
  const aujourdhui = useMemo(() => new Date(), []);
  const [choix, setChoix] = useState<Preselection>(DEFAUT);
  const [periode, setPeriode] = useState<Periode>(
    () => bornes(DEFAUT, new Date()) ?? { since: "", until: "" },
  );
  const [cahier, setCahier] = useState<WorkbookReport | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    if (!periode.since || !periode.until) return;
    let annule = false;
    setErreur(null);
    // Comme l'écran d'effort : le contenu précédent reste pendant le chargement
    // du suivant, pour qu'une comparaison entre deux périodes ne clignote pas.
    void (async () => {
      try {
        const charge = await call("cahierDeTravail", {
          params: { child_id: childId },
          query: { since: periode.since, until: periode.until },
        });
        if (!annule) setCahier(charge);
      } catch (echec) {
        if (!annule) {
          setErreur(echec instanceof ApiError ? echec.message : "Chargement impossible.");
        }
      }
    })();
    return () => {
      annule = true;
    };
  }, [childId, periode.since, periode.until]);

  const choisir = (valeur: Preselection) => {
    setChoix(valeur);
    const calcule = bornes(valeur, aujourdhui);
    if (calcule) setPeriode(calcule);
  };

  const competences = cahier?.competencies ?? [];

  return (
    <Screen
      title="Compétences et exercices"
      lead={
        periode.since && periode.until
          ? `Du ${enClair(periode.since)} au ${enClair(periode.until)}`
          : undefined
      }
    >
      <SelecteurDePeriode
        choix={choix}
        periode={periode}
        max={jour(aujourdhui)}
        onChoisir={choisir}
        onDates={(suivante) => {
          setChoix("libre");
          setPeriode(suivante);
        }}
      />

      {erreur ? <ErrorNote>{erreur}</ErrorNote> : null}
      {!cahier && !erreur ? <Loading /> : null}

      {cahier && competences.length === 0 ? (
        <Card>
          {/* La période est répétée ici, et pas seulement en tête : « rien » sur
              sept jours et « rien » depuis la rentrée ne se lisent pas pareil. */}
          <Empty>
            Aucune compétence travaillée du {enClair(cahier.since)} au {enClair(cahier.until)}.
          </Empty>
        </Card>
      ) : null}

      {competences.map((competence) => (
        <CarteCompetence key={competence.competency_id} competence={competence} />
      ))}
    </Screen>
  );
}

function CarteCompetence({ competence }: { competence: CompetencyWorkbookOut }) {
  const exercices = competence.exercises ?? [];
  const temps = duree(competence.duration_s);

  return (
    <Card title={competence.label}>
      <p className="muted small">
        {etiquette(competence.subject)} · {exercices.length} exercice
        {exercices.length > 1 ? "s" : ""} · {competence.attempts} réponse
        {competence.attempts > 1 ? "s" : ""} envoyée{competence.attempts > 1 ? "s" : ""}
        {temps ? ` · ${temps}` : ""}
      </p>

      <p className="muted small">
        {competence.solved} exercice{competence.solved > 1 ? "s" : ""} résolu
        {competence.solved > 1 ? "s" : ""} sur {exercices.length} · <Reserve competence={competence} />
      </p>

      {exercices.length === 0 ? (
        <Empty>Aucun exercice rendu sur cette période.</Empty>
      ) : (
        <details className="deep">
          <summary>Les énoncés travaillés</summary>
          <ul className="exercices">
            {exercices.map((exercice) => (
              <Enonce key={exercice.exercise_id} exercice={exercice} />
            ))}
          </ul>
        </details>
      )}
    </Card>
  );
}

/**
 * Ce qu'il reste en réserve, dit en deux nombres.
 *
 * Zéro en stock n'est pas une panne d'affichage : c'est une compétence dont le
 * pool est vide, et c'est précisément ce qu'un parent doit pouvoir constater
 * — le travail du soir s'arrêtera dessus.
 */
function Reserve({ competence }: { competence: CompetencyWorkbookOut }) {
  if (competence.available === 0) return <>aucun exercice en réserve</>;
  return (
    <>
      {competence.available} en réserve, dont {competence.never_served} jamais servi
      {competence.never_served > 1 ? "s" : ""}
    </>
  );
}

const RESULTAT: Record<string, string> = {
  success: "résolu",
  failure: "non résolu",
  abandon: "abandonné",
};

function Enonce({ exercice }: { exercice: WorkedExerciseOut }) {
  const temps = duree(exercice.duration_s);
  const indices =
    exercice.hints_used === 0
      ? "aucun indice"
      : exercice.hints_used === 1
        ? "1 indice"
        : `${exercice.hints_used} indices`;

  return (
    <li className="exercice">
      <TexteMathematique className="exercice__enonce">{exercice.statement}</TexteMathematique>
      {exercice.origin === "livre" ? (
        <p className="muted small">Énoncé issu d'un ouvrage : l'enfant l'a lu en image.</p>
      ) : null}
      <p className="muted small">
        {/* Le résultat, jamais une couleur : demander trois indices et finir par
            trouver **est** du travail (même règle que l'écran d'effort). */}
        {exercice.outcome ? RESULTAT[exercice.outcome] ?? exercice.outcome : "sans résultat enregistré"}{" "}
        · {exercice.attempts} réponse{exercice.attempts > 1 ? "s" : ""} · {indices}
        {temps ? ` · ${temps}` : ""}
      </p>
    </li>
  );
}
